/**
 * Effective working hours of the sites in a usage report's scope (ADR 0018): the "Mesai saatine göre" basis
 * label shows them instead of a fixed "08–18". Resolved per site by effective_alert_settings (inherited,
 * also through ancestors the user cannot see; default Mon–Fri 08:00–18:00), like the report itself.
 */
import type { AlertHours } from "@fts/core";
import type { DataProvider } from "../../data";
import type { TFn } from "../../i18n";
import { formatDays } from "../../lib/alarms";
import type { TenantData } from "../../state/session";

export type WorkHoursInfo = {
  /** "08:00–18:00" when every site in scope has the same hours; null when they differ. */
  uniform: string | null;
  /** One line per site: [site name, "Pzt–Cum 08:00–18:00"]. */
  perSite: [string, string][];
};

/** Sites whose vehicles the report can cover: the selected subtree (or all), narrowed to the picked vehicles. */
export function scopeSiteIds(data: TenantData, siteId: string | null, vehicleIds: string[]): string[] {
  let inTree: Set<string>;
  if (siteId) {
    inTree = new Set([siteId]);
    for (let grew = true; grew; ) {
      grew = false;
      for (const s of data.sites) if (s.parentId && inTree.has(s.parentId) && !inTree.has(s.id)) (inTree.add(s.id), (grew = true));
    }
  } else inTree = new Set(data.sites.map((s) => s.id));
  const vehicles = data.vehicles.filter((v) => inTree.has(v.siteId) && v.deviceId && (!vehicleIds.length || vehicleIds.includes(v.id)));
  const ids = [...new Set(vehicles.map((v) => v.siteId))];
  return ids.length ? ids : [...inTree];
}

const span = (h: AlertHours) => `${h.workStart}–${h.workEnd}`;

export async function loadWorkHours(provider: DataProvider, t: TFn, data: TenantData, siteIds: string[]): Promise<WorkHoursInfo> {
  const res = await Promise.all(siteIds.map(async (id) => ({ id, eff: await provider.getEffectiveAlertSettings(id).catch(() => null) })));
  const known = res.filter((r): r is { id: string; eff: NonNullable<typeof r.eff> } => r.eff !== null);
  const name = (id: string) => data.sites.find((s) => s.id === id)?.name ?? id;
  const perSite: [string, string][] = known
    .map((r): [string, string] => [name(r.id), `${formatDays(t, r.eff.hours.workDays)} ${span(r.eff.hours)}`])
    .sort((a, b) => a[0].localeCompare(b[0]));
  const keys = new Set(known.map((r) => `${[...r.eff.hours.workDays].sort().join(",")}|${span(r.eff.hours)}`));
  return { uniform: keys.size === 1 ? span(known[0]!.eff.hours) : null, perSite };
}

/** Label of a basis option; "work" names the effective hours (or says they vary by site). */
export function basisOptionLabel(t: TFn, basis: string, wh: WorkHoursInfo | null | undefined): string {
  if (basis !== "work" || !wh || !wh.perSite.length) return t(`reports.basisOption.${basis}` as Parameters<TFn>[0]);
  return wh.uniform ? t("reports.basisOption.workAt", { hours: wh.uniform }) : t("reports.basisOption.workVaries");
}

/** Per-site hours for the tooltip and the export summary (only when they differ). */
export function perSiteText(wh: WorkHoursInfo | null | undefined): string | null {
  return wh && !wh.uniform && wh.perSite.length ? wh.perSite.map(([s, h]) => `${s}: ${h}`).join("; ") : null;
}
